import type React from "react"
import { useNotificationPreferences } from "../hooks/use-notification-preferences"
import { useProperties } from "../hooks/use-properties"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useAuthenticatedUser } from "../user-context"
import { NotificationPreferencesSection } from "./notification-preferences-section"

/**
 * Owns the data wiring for notification preferences so the Settings page
 * stays a thin shell. Resolves the signed-in user itself — callers pass
 * nothing.
 */
export const NotificationPreferencesPanel: React.FC = () => {
	const { profile } = useAuthenticatedUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const properties = useProperties()
	const notificationPreferences = useNotificationPreferences(profile.id)
	const currentTimeZone =
		Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"

	return (
		<NotificationPreferencesSection
			copy={{
				title: t.settings.notifications.title,
				masterToggle: t.settings.notifications.masterToggle,
				quietHoursToggle: t.settings.notifications.quietHoursToggle,
				quietHoursStart: t.settings.notifications.quietHoursStart,
				quietHoursEnd: t.settings.notifications.quietHoursEnd,
				propertiesTitle: t.settings.notifications.propertiesTitle,
				muteProperty: (name) =>
					t.settings.notifications.muteProperty.replace("{property}", name),
				save: t.settings.notifications.save,
				savedTimeZone: t.settings.notifications.savedTimeZone,
				loading: t.settings.notifications.loading,
				error: t.settings.notifications.error,
			}}
			currentTimeZone={currentTimeZone}
			error={
				notificationPreferences.error || notificationPreferences.saveError
					? t.settings.notifications.error
					: null
			}
			onSave={async (body) => {
				await notificationPreferences.save(body)
			}}
			preferences={notificationPreferences.preferences}
			properties={properties.data ?? []}
			saving={notificationPreferences.isSaving}
		/>
	)
}
